import React, { useState, useEffect } from "react";
import { Trash2, CheckCircle, XCircle, Shield, Loader2 } from "lucide-react";
import Button from "../ui/Button";
import Badge from "../ui/Badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";

const UserManagementTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/list?limit=1000`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Sei sicuro di voler cancellare l'utente ${email}? Questa azione è irreversibile.`)) {
      return;
    }

    try {
      setActionLoading(`delete_${userId}`);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert("✅ Utente cancellato con successo!");
        fetchUsers();
      } else {
        const error = await response.json();
        alert(`❌ Errore: ${error.detail}`);
      }
    } catch (error) {
      alert("❌ Errore di rete");
    } finally {
      setActionLoading(null);
    }
  };

  const handleGrantAccess = async (userId, email, productId) => {
    if (!window.confirm(`Attivare ${productId} per ${email}?`)) return;

    try {
      setActionLoading(`grant_${userId}_${productId}`);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/grant-access`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            user_email: email,
            product_id: productId,
          }),
        }
      );

      if (response.ok) {
        alert(`✅ ${productId} attivato per ${email}!`);
        fetchUsers();
      } else {
        const error = await response.json();
        alert(`❌ Errore: ${error.detail}`);
      }
    } catch (error) {
      alert("❌ Errore di rete");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevokeAccess = async (userId, productId, email) => {
    if (!window.confirm(`Revocare accesso a ${productId} per ${email}?`)) return;

    try {
      setActionLoading(`revoke_${userId}_${productId}`);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/revoke-access/${productId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert("✅ Accesso revocato!");
        fetchUsers();
      } else {
        const error = await response.json();
        alert(`❌ Errore: ${error.detail}`);
      }
    } catch (error) {
      alert("❌ Errore di rete");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleRole = async (userId, currentRole, email) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    if (!window.confirm(`Cambiare ruolo di ${email} a ${newRole}?`)) return;

    try {
      setActionLoading(`role_${userId}`);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/role?role=${newRole}`,
        {
          method: "PATCH",
          credentials: "include",
        }
      );

      if (response.ok) {
        alert(`✅ Ruolo aggiornato a ${newRole}!`);
        fetchUsers();
      } else {
        const error = await response.json();
        alert(`❌ Errore: ${error.detail}`);
      }
    } catch (error) {
      alert("❌ Errore di rete");
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Caricamento utenti...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center">
            <Shield className="w-5 h-5 mr-2 text-ruby" />
            Gestione Utenti ({users.length})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {users.map((userData) => (
            <div
              key={userData.user_id}
              className="p-4 border border-border rounded-lg hover:border-gold/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <p className="font-heading text-foreground">{userData.name}</p>
                    <Badge variant={userData.role === "admin" ? "ruby" : "gold"}>
                      {userData.role}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{userData.email}</p>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>{userData.order_count || 0} ordini</span>
                    <span>Registrato: {new Date(userData.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleToggleRole(userData.user_id, userData.role, userData.email)}
                    variant={userData.role === "admin" ? "ghost" : "ruby"}
                    size="sm"
                    disabled={actionLoading === `role_${userData.user_id}`}
                  >
                    {userData.role === "admin" ? "⬇️ Retrocedi" : "⬆️ Promuovi Admin"}
                  </Button>
                  
                  <Button
                    onClick={() => handleDeleteUser(userData.user_id, userData.email)}
                    variant="ghost"
                    size="sm"
                    className="text-ruby hover:bg-ruby/10"
                    disabled={actionLoading === `delete_${userData.user_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Product Access Control */}
              <div className="mt-4 p-3 bg-background/50 rounded-lg">
                <p className="text-xs font-heading text-muted-foreground mb-2">ACCESSO WEBAPP:</p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => handleGrantAccess(userData.user_id, userData.email, "cartomancer_basic")}
                    variant="gold"
                    size="sm"
                    disabled={actionLoading === `grant_${userData.user_id}_cartomancer_basic`}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Attiva Basic
                  </Button>
                  <Button
                    onClick={() => handleGrantAccess(userData.user_id, userData.email, "cartomancer_premium")}
                    variant="gold"
                    size="sm"
                    disabled={actionLoading === `grant_${userData.user_id}_cartomancer_premium`}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Attiva Premium
                  </Button>
                  <Button
                    onClick={() => handleRevokeAccess(userData.user_id, "cartomancer_basic", userData.email)}
                    variant="ghost"
                    size="sm"
                    className="text-ruby"
                    disabled={actionLoading === `revoke_${userData.user_id}_cartomancer_basic`}
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    Revoca Basic
                  </Button>
                  <Button
                    onClick={() => handleRevokeAccess(userData.user_id, "cartomancer_premium", userData.email)}
                    variant="ghost"
                    size="sm"
                    className="text-ruby"
                    disabled={actionLoading === `revoke_${userData.user_id}_cartomancer_premium`}
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    Revoca Premium
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {users.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Nessun utente registrato</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UserManagementTab;
